// src/layout/NotificheDropdown.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import { getNotifiche, leggiNotifica, leggiTutte } from '../services/notificheApi';
import { getRichiesteCount } from '../services/shopAdminApi';

// Icone per tipo notifica
const TIPO_CONFIG = {
    preventivo_scadenza:  { emoji: '⏰', colore: '#f59e0b' },
    ordine_pronto:        { emoji: '✅', colore: '#10b981' },
    scorta_bassa:         { emoji: '📦', colore: '#ef4444' },
    preventivo_accettato: { emoji: '🎉', colore: '#3b82f6' },
    ordine_urgente:       { emoji: '🚨', colore: '#dc2626' },
    fase_assegnata:       { emoji: '👉', colore: '#00a3e0' },
    fase_da_assegnare:    { emoji: '🙋', colore: '#e6007e' },
    fase_da_decidere:     { emoji: '❓', colore: '#f59e0b' },
    bozza_approvata:      { emoji: '👍', colore: '#10b981' },
    bozza_da_correggere:  { emoji: '✏️', colore: '#e6007e' },
};

// Converte un timestamp in testo relativo ("2 ore fa", "ieri", ecc.)
function tempoRelativo(dataStr) {
    const diff = Date.now() - new Date(dataStr).getTime();
    const min  = Math.floor(diff / 60000);
    if (min < 1)   return 'adesso';
    if (min < 60)  return `${min} min fa`;
    const ore = Math.floor(min / 60);
    if (ore < 24)  return `${ore} ora${ore > 1 ? ' fa' : ' fa'}`;
    const gg = Math.floor(ore / 24);
    if (gg === 1)  return 'ieri';
    return `${gg} giorni fa`;
}

// Restituisce il path di navigazione in base al tipo e all'entità
function getLink(tipo, entita_tipo, entita_id) {
    if (tipo === 'preventivo_scadenza' || tipo === 'preventivo_accettato') return '/preventivi';
    if (tipo === 'ordine_pronto'       || tipo === 'ordine_urgente')        return entita_id ? `/ordini/${entita_id}` : '/ordini';
    if (String(tipo).startsWith('fase_') || String(tipo).startsWith('bozza_')) return entita_id ? `/ordini/${entita_id}` : '/produzione';
    if (tipo === 'scorta_bassa')        return '/magazzino';
    return '/dashboard';
}

const NotificheDropdown = () => {
    const [notifiche,     setNotifiche]     = useState([]);
    const [aperto,        setAperto]        = useState(false);
    const [caricamento,   setCaricamento]   = useState(false);
    const [richiesteShop, setRichiesteShop] = useState(0);
    const dropdownRef = useRef(null);
    const navigate    = useNavigate();

    const nonLette = notifiche.filter(n => !n.letta).length;

    // ── Carica notifiche dal server ──────────────────────────────────────────
    const carica = useCallback(async () => {
        try {
            const data = await getNotifiche();
            // Prima quelle per te (fasi assegnate a te o al tuo ruolo), poi quelle dell'account (PSC-182)
            const elenco = Array.isArray(data) ? data : [];
            setNotifiche([...elenco.filter(n => n.per_me), ...elenco.filter(n => !n.per_me)]);
        } catch {
            // silent: backend non disponibile
        }
        getRichiesteCount().then(d => setRichiesteShop(d.count || 0)).catch(() => {});
    }, []);

    // Carica al mount + polling ogni 60 s
    useEffect(() => {
        carica();
        const timer = setInterval(carica, 60_000);
        return () => clearInterval(timer);
    }, [carica]);

    // Chiudi dropdown se si clicca fuori
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setAperto(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Segna una singola notifica come letta e naviga ──────────────────────
    const handleClick = async (notifica) => {
        if (!notifica.letta) {
            try {
                await leggiNotifica(notifica.id);
                setNotifiche(prev =>
                    prev.map(n => n.id === notifica.id ? { ...n, letta: 1 } : n)
                );
            } catch (err) {
                console.error('Errore segna letta:', err);
            }
        }
        setAperto(false);
        navigate(getLink(notifica.tipo, notifica.entita_tipo, notifica.entita_id));
    };

    // ── Segna tutte come lette ───────────────────────────────────────────────
    const handleLeggiTutte = async (e) => {
        e.stopPropagation();
        setCaricamento(true);
        try {
            await leggiTutte();
            setNotifiche(prev => prev.map(n => ({ ...n, letta: 1 })));
        } catch (err) {
            console.error('Errore segna tutte lette:', err);
        } finally {
            setCaricamento(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="notifiche-wrapper position-relative" ref={dropdownRef}>

            {/* Campanella */}
            <button
                className="btn btn-sm btn-light position-relative"
                onClick={() => setAperto(v => !v)}
                title="Notifiche"
                style={{ zIndex: 1050 }}
            >
                <FaBell size={18} />
                {nonLette > 0 && (
                    <span
                        className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                        style={{ fontSize: '0.65rem', minWidth: '18px', padding: '2px 5px' }}
                    >
                        {nonLette > 99 ? '99+' : nonLette}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {aperto && (
                <div
                    className="notifiche-dropdown shadow-lg border rounded bg-white"
                    style={{
                        position:  'absolute',
                        top:       'calc(100% + 8px)',
                        right:     0,
                        width:     '360px',
                        zIndex:    1060,
                    }}
                >
                    {/* Header dropdown */}
                    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                        <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                            Notifiche {nonLette > 0 && (
                            <span className="badge bg-danger ms-1">{nonLette}</span>
                        )}
                        </span>
                        {nonLette > 0 && (
                            <button
                                className="btn btn-link btn-sm p-0 text-secondary"
                                style={{ fontSize: '0.78rem', textDecoration: 'none' }}
                                onClick={handleLeggiTutte}
                                disabled={caricamento}
                            >
                                {caricamento ? 'Caricamento…' : 'Segna tutte come lette'}
                            </button>
                        )}
                    </div>

                    {/* Lista notifiche */}
                    <div
                        className="notifiche-lista overflow-auto"
                        style={{ maxHeight: '400px' }}
                    >
                        {notifiche.length === 0 ? (
                            <div className="text-center text-muted py-4" style={{ fontSize: '0.85rem' }}>
                                <FaBell size={28} className="mb-2 opacity-25" />
                                <div>Nessuna notifica</div>
                            </div>
                        ) : (
                            notifiche.map(n => {
                                const cfg   = TIPO_CONFIG[n.tipo] ?? { emoji: '🔔', colore: '#6b7280' };
                                const letta = !!n.letta;
                                return (
                                    <div
                                        key={n.id}
                                        onClick={() => handleClick(n)}
                                        className="d-flex align-items-start px-3 py-2 border-bottom notifica-item"
                                        style={{
                                            cursor:     'pointer',
                                            background: letta ? 'transparent' : '#f0f7ff',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = letta ? '#f9fafb' : '#dbeafe'}
                                        onMouseLeave={e => e.currentTarget.style.background = letta ? 'transparent' : '#f0f7ff'}
                                    >
                                        {/* Icona tipo */}
                                        <div
                                            className="me-2 mt-1 flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                width:      '32px',
                                                height:     '32px',
                                                background: cfg.colore + '22',
                                                fontSize:   '1rem',
                                            }}
                                        >
                                            {cfg.emoji}
                                        </div>

                                        {/* Testo */}
                                        <div className="flex-grow-1 min-width-0">
                                            <div
                                                className="fw-semibold text-truncate"
                                                style={{
                                                    fontSize: '0.82rem',
                                                    color:    letta ? '#6b7280' : '#111827',
                                                }}
                                            >
                                                {n.per_me && <span className="badge me-1" style={{ background: '#00a3e0', fontSize: '0.62rem' }}>Per te</span>}
                                                {n.titolo}
                                            </div>
                                            {n.messaggio && (
                                                <div
                                                    className="text-muted text-truncate"
                                                    style={{ fontSize: '0.75rem', maxWidth: '260px' }}
                                                >
                                                    {n.messaggio}
                                                </div>
                                            )}
                                            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px' }}>
                                                {tempoRelativo(n.created_at)}
                                            </div>
                                        </div>

                                        {/* Pallino non letta */}
                                        {!letta && (
                                            <div
                                                className="ms-2 mt-1 flex-shrink-0 rounded-circle"
                                                style={{
                                                    width:      '8px',
                                                    height:     '8px',
                                                    background: cfg.colore,
                                                    marginTop:  '6px',
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Richieste shop */}
                    {richiesteShop > 0 && (
                        <div
                            className="d-flex align-items-center px-3 py-2 border-bottom"
                            style={{ cursor: 'pointer', background: '#fff7ed' }}
                            onClick={() => { setAperto(false); navigate('/shop/richieste'); }}
                        >
                            <div
                                className="me-2 flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle"
                                style={{ width: '32px', height: '32px', background: '#fed7aa', fontSize: '1rem' }}
                            >
                                🛒
                            </div>
                            <div className="flex-grow-1">
                                <div className="fw-semibold" style={{ fontSize: '0.82rem', color: '#c2410c' }}>
                                    {richiesteShop} {richiesteShop === 1 ? 'nuova richiesta' : 'nuove richieste'} dallo shop
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Clicca per gestirle</div>
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    {notifiche.length > 0 && (
                        <div className="text-center py-2 border-top">
                            <button
                                className="btn btn-link btn-sm text-secondary p-0"
                                style={{ fontSize: '0.78rem', textDecoration: 'none' }}
                                onClick={() => { setAperto(false); navigate('/dashboard'); }}
                            >
                                Vai alla dashboard →
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificheDropdown;